// The check-in surface's binding of the shared expected-vs-unexpected classification
// (`me/expectedOutcome.ts`), mirroring `flows/feedback/feedbackOutcome.ts`.
//
// `PUT /me/sessions/{uuid}/checkin` has TWO 409 exits: the window is closed (a prose `detail`,
// guarded by `checkin_window_open`), and the partner already saved (the stable CODE
// `reflection_already_submitted`). The second is not a failure (the answers are saved), so the
// flow checks `isAlreadySubmitted` first and shows its saved screen. Any OTHER 409 is the closed
// window; reading the status there is more durable than string-matching its sentence.

import { ApiError } from "@/api/client";
import { unexpectedDetail, type ExpectedFailure } from "../../expectedOutcome";
import { isAlreadySubmitted } from "../../hooks/reflectionHooks";
import { CHECKIN_CLOSED } from "./checkinCopy";

/** The conflict status: the window has closed, unless the code says already saved. */
const WINDOW_CLOSED_STATUS = 409;

/**
 * Classify one check-in failure, or `null` when there is no error at all.
 *
 * `fallbackDetail` is what a NON-`Error` rejection reports; see `unexpectedDetail` for why it
 * must never be `null`.
 */
export function classifyCheckinError(
  error: unknown,
  fallbackDetail = "",
): ExpectedFailure | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status === WINDOW_CLOSED_STATUS && !isAlreadySubmitted(error)) {
    return { kind: "expected", outcome: CHECKIN_CLOSED };
  }
  return { kind: "unexpected", detail: unexpectedDetail(error, fallbackDetail) };
}
